import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import API from "../api";

const MAX_TRIES = 8;

const PaymentSuccess = () => {
  const { t, clearCart } = useApp();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [order, setOrder] = useState(null);
  const triesRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      if (triesRef.current >= MAX_TRIES) { setState("timeout"); return; }
      triesRef.current += 1;
      try {
        const r = await API.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          setOrder(r.data);
          if (!clearedRef.current) { clearCart(); clearedRef.current = true; }
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center fade-up">
      {state === "polling" && (
        <>
          <Loader2 className="h-12 w-12 mx-auto mb-6 text-[#b08d57] animate-spin" strokeWidth={1.5} />
          <h1 className="font-serif-lux text-3xl mb-2">Confirmation du paiement…</h1>
          <p className="text-sm text-muted-foreground">Merci de patienter quelques instants.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="h-16 w-16 rounded-full bg-[#2f7d4f]/12 flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-[#2f7d4f]" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif-lux text-3xl md:text-4xl mb-3">{t("orderPlaced")}</h1>
          {order?.order_id && <p className="text-sm text-[#555] mb-6">{t("orderNumber")} #{order.order_id.slice(0, 8).toUpperCase()}</p>}
          <div className="flex gap-3 justify-center">
            <Link to="/account" className="bg-[#1b1b1b] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("myOrders")}</Link>
            <Link to="/c/all" className="border border-[#1b1b1b] px-6 py-3 text-sm tracking-wide hover:bg-[#1b1b1b] hover:text-white transition-colors">{t("continueShopping")}</Link>
          </div>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle className="h-12 w-12 mx-auto mb-6 text-[#8b1e3f]" strokeWidth={1.5} />
          <h1 className="font-serif-lux text-3xl mb-3">{state === "timeout" ? "Paiement en cours de vérification" : "Un problème est survenu"}</h1>
          <p className="text-sm text-[#555] mb-6">{state === "timeout" ? "Votre paiement est en cours de traitement. Consultez vos commandes dans quelques instants." : "Le paiement n'a pas pu être confirmé."}</p>
          <Link to="/account" className="inline-block bg-[#1b1b1b] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#b08d57] transition-colors">{t("myOrders")}</Link>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
